package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.repository.PlaceRepository
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.CredentialAlreadyInUseException
import fyi.moko.hotpl.domain.service.RemotePlaceCatalog
import fyi.moko.hotpl.domain.service.SyncGate
import kotlinx.coroutines.CancellationException

/**
 * Links an anonymous session to a Google/Apple account, orchestrating the two
 * Phase 3 scenarios from `docs/implementation_plan.md`:
 *
 * - **A — first-time link (happy path):** the credential is free, so it attaches
 *   to the current user. The uid is unchanged and local/remote data continues
 *   seamlessly -> [Outcome.Linked].
 * - **B — credential already in use (conflict):** the credential already belongs
 *   to a different account (e.g. the user's previous device). Linking fails with
 *   [CredentialAlreadyInUseException]; we instead **sign in** to that account,
 *   which changes the uid. If the previous (anonymous) session owned no local places
 *   there is nothing to decide, so it's re-branded automatically -> [Outcome.SwitchedAccount].
 *   If it owned any, merging them in automatically risks a visible duplicate of a place the
 *   target account already saved, so this pauses at [Outcome.NeedsSwitchDecision] for the
 *   caller to ask the user keep-vs-discard and call [resolveSwitchDecision] with the answer.
 *
 * After a switch the existing remote dataset pulls in through the normal
 * [fyi.moko.hotpl.data.service.SyncCoordinator] path; the `savePlaceFromRemote`
 * non-enqueuing boundary keeps that pull from re-exploding the sync queue.
 *
 * The switch runs behind [SyncGate]: the coordinator would otherwise attach a listener for the
 * new uid and start pushing the moment sign-in lands, racing the re-branding below. Local place
 * ids that the target account already uses ([RemotePlaceCatalog]) are remapped out of the way, so
 * the incoming dataset can't overwrite an unrelated place that happens to share its id.
 */
class LinkAccountUseCase(
    private val authService: AuthService,
    private val placeRepository: PlaceRepository,
    private val remotePlaceCatalog: RemotePlaceCatalog,
    private val syncGate: SyncGate,
) {
    enum class Provider { GOOGLE, APPLE }

    /**
     * Thrown when an account switch signed into [newUid] but could not read the target account's
     * remote place ids, leaving local places owned by the previous uid. The sync engine will adopt
     * them automatically once the app is online and restarted.
     */
    class AccountSwitchIncompleteException(
        val newUid: String,
        cause: Throwable,
    ) : Exception(
        "Signed in as $newUid, but your local places haven't finished moving to this account yet. " +
            "Keep the app online and restart it to complete the transfer safely.",
        cause
    )

    /**
     * The tokens a platform sign-in flow produces. [idToken] is the OAuth ID token (a JWT);
     * [accessToken] is the OAuth access token, which is only available (and only required)
     * on some platforms — see [AuthService.linkWithGoogle]. [rawNonce] is Apple-only — see
     * [AuthService.linkWithApple].
     */
    data class SocialCredential(val idToken: String, val accessToken: String? = null, val rawNonce: String? = null)

    sealed interface Outcome {
        /** Credential linked to the existing user; uid unchanged. */
        object Linked : Outcome

        /**
         * Credential already belonged to another account; signed into it instead.
         * Local temp places were re-branded from [previousUid] to [newUid] (or, if the user chose
         * to discard them, deleted — see [resolveSwitchDecision]).
         */
        data class SwitchedAccount(val previousUid: String?, val newUid: String) : Outcome

        /**
         * The switch already landed — auth is now [newUid] — but [previousUid] owns
         * [localPlaceCount] local places, so merging them in automatically could produce visible
         * duplicates of places the target account already has. The caller must ask the user
         * whether to keep those places (carry them into the account) or discard them (use only
         * the account's existing data), then call [resolveSwitchDecision] with the answer to
         * finish. Never emitted when [previousUid] owns no local places — nothing to ask about.
         */
        data class NeedsSwitchDecision(val previousUid: String, val newUid: String, val localPlaceCount: Int) : Outcome
    }

    suspend operator fun invoke(provider: Provider, credential: SocialCredential): Result<Outcome> {
        if (provider == Provider.APPLE && credential.rawNonce == null) {
            return Result.failure(IllegalArgumentException("Apple credential is missing its rawNonce"))
        }
        val previousUid = authService.getCurrentUserId()

        if (provider == Provider.APPLE) {
            // Apple ID tokens are single-use: a link attempt that fails with a collision has
            // already spent the token, so the Google-style link → sign-in-with-the-same-token
            // fallback below is always rejected with 17094 "Duplicate credential received".
            // AuthService.linkOrSignInWithApple resolves the collision in one operation (iOS
            // signs in with the rotated credential Firebase attaches to the error); the whole
            // call runs behind the gate because a switch may land mid-way through it.
            return syncGate.withPause {
                authService.linkOrSignInWithApple(credential.idToken, credential.rawNonce!!).fold(
                    onSuccess = { linked ->
                        when (linked) {
                            AuthService.AppleLinkResult.Linked -> Result.success(Outcome.Linked)
                            is AuthService.AppleLinkResult.SwitchedTo -> completeSwitch(previousUid, linked.uid)
                        }
                    },
                    onFailure = { Result.failure(it) },
                )
            }
        }

        if (previousUid != null) {
            val linkResult = authService.linkWithGoogle(credential.idToken, credential.accessToken)
            if (linkResult.isSuccess) {
                return Result.success(Outcome.Linked)
            }

            val error = linkResult.exceptionOrNull()
            if (error !is CredentialAlreadyInUseException) {
                return Result.failure(error ?: IllegalStateException("Account link failed"))
            }
        }
        // previousUid == null: there is no session to link (the anonymous bootstrap runs once at
        // launch and can fail silently, and a sign-out leaves the app userless until restart), so
        // linking can only dead-end with "No current user to link" — sign in with the credential
        // directly instead. completeSwitch handles the null previousUid by skipping the rebrand;
        // any local places carry a blank ownerId and are adopted by the coordinator's
        // brandLocalPlacesWithUser once the new uid lands.
        //
        // Conflict: switch into the existing account (uid changes), then carry local data over.
        // Sync stays parked for the whole switch so the coordinator can't pull the target
        // account's places (or push half-rebranded ones) while ownership is being rewritten.
        return syncGate.withPause {
            val signInResult = authService.signInWithGoogle(credential.idToken, credential.accessToken)
            val newUid = signInResult.getOrElse { return@withPause Result.failure(it) }
            completeSwitch(previousUid, newUid)
        }
    }

    /**
     * Post-sign-in bookkeeping shared by every switch path. When [previousUid] owns local
     * places, merging them in automatically risks a visible duplicate (the target account may
     * already have saved the same real places), so this pauses for a decision instead of
     * guessing — see [Outcome.NeedsSwitchDecision]. Callers must already hold the [syncGate]
     * pause.
     */
    private suspend fun completeSwitch(previousUid: String?, newUid: String): Result<Outcome> {
        if (previousUid == null || previousUid == newUid) {
            return Result.success(Outcome.SwitchedAccount(previousUid, newUid))
        }
        val localCount = placeRepository.countPlacesOwnedBy(previousUid)
        if (localCount == 0) {
            return finishSwitch(previousUid, newUid, keepLocalData = true)
        }
        return Result.success(Outcome.NeedsSwitchDecision(previousUid, newUid, localCount))
    }

    /**
     * Finishes a switch that returned [Outcome.NeedsSwitchDecision], after the caller has asked
     * the user whether to [keepLocalData]. Runs its own [SyncGate] pause: unlike the rest of the
     * switch, this can happen an arbitrary amount of time after sign-in (however long the user
     * takes to answer), so it must not hold the gate for that whole gap — only for the brief
     * mutation itself.
     */
    suspend fun resolveSwitchDecision(
        previousUid: String,
        newUid: String,
        keepLocalData: Boolean,
    ): Result<Outcome> = syncGate.withPause { finishSwitch(previousUid, newUid, keepLocalData) }

    private suspend fun finishSwitch(previousUid: String, newUid: String, keepLocalData: Boolean): Result<Outcome> {
        if (!keepLocalData) {
            // The user chose the account's existing data over the anonymous session's local
            // places. previousUid is now an abandoned session that will never authenticate
            // again, so there is nothing to carry over and no one to push a tombstone to —
            // just remove the local rows.
            placeRepository.discardPlacesOwnedBy(previousUid)
            return Result.success(Outcome.SwitchedAccount(previousUid, newUid))
        }
        // Reading the target account's ids can only fail if the network dropped between
        // sign-in and now. Rebranding blind would risk the incoming dataset silently
        // overwriting a colliding local place, so surface it instead.
        val reservedIds = try {
            remotePlaceCatalog.placeIdsFor(newUid)
        } catch (e: CancellationException) {
            // An auth-switch cancellation (ChildCancelledException etc.) is expected when
            // the user re-triggers a link while a previous switch is still in flight, or
            // the SyncGate pause races a scope cancellation. It is not a failure — surface
            // it as an info log, and propagate the cancellation so the caller's scope
            // unwinds cleanly instead of presenting "account switch incomplete" to the user.
            println("HotPl link: completeSwitch cancelled (auth-switch) — newUidTail=<${newUid.takeLast(4)}>")
            throw e
        } catch (e: Exception) {
            return Result.failure(AccountSwitchIncompleteException(newUid, e))
        }
        // Best-effort: unlike reservedIds (whose absence risks silently overwriting a
        // colliding place), a failure here just means dedup doesn't happen and the switch
        // proceeds with a possible visible duplicate rather than failing the whole switch.
        val duplicateKeys = try {
            remotePlaceCatalog.duplicateKeysFor(newUid)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            emptyMap()
        }
        placeRepository.rebrandPlaces(
            fromUid = previousUid,
            toUid = newUid,
            reservedIds = reservedIds,
            duplicateKeys = duplicateKeys,
        )
        return Result.success(Outcome.SwitchedAccount(previousUid, newUid))
    }
}
